import { useShipmentsApi } from "@/Hooks/useShipmentsApi";
import { Link } from "@freightsimple/ui-core/Typography/Link";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { isEmptyOrUndefined } from "@/Helpers/isNotEmptyOrUndefined";
import type { ReactNode } from "react";
import { useRef, useState } from "react";

interface Result {
  isValid: boolean;
  message?: string | ReactNode | undefined;
  count?: number | undefined;
}

const invalidResult: Result = {
  isValid: false,
};

export interface UseValidateReferenceNumberReturn {
  isLoading: boolean;
  result: Result;
  validate: (_: string | undefined) => void;
}

export function useReferenceNumbersValidate({
  locationContext,
  originalReferenceNumber,
  shipmentId,
}: {
  locationContext: LocationContext | undefined;
  originalReferenceNumber: string | undefined;
  shipmentId?: string | undefined;
}): UseValidateReferenceNumberReturn {
  const [result, setResult] = useState({ ...invalidResult });
  const [isLoading, setIsLoading] = useState(false);
  const shipmentApi = useShipmentsApi();

  const timeoutId = useRef<NodeJS.Timeout | undefined>(undefined);

  const REFERENCE_NUMBER_MIN_CHARS = 5;

  async function serverValidation(referenceNumber: string) {
    if (locationContext === undefined) {
      console.error("Unexpected location context");
      return;
    }

    setIsLoading(true);
    try {
      const response = await shipmentApi.lookUpReferenceNumber({
        locationContext,
        referenceNumber,
      });

      const isNotReused =
        response.shipmentIds.filter((id) => id !== shipmentId).length === 0;

      if (response.isValid || isNotReused) {
        setValid();
      } else {
        setInvalid(
          referenceNumber,
          response.message,
          response.shipmentIds.length,
        );
      }
    } catch (e) {
      console.error(e);
    }
    setIsLoading(false);
  }

  function clearQueuedValidation() {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
      timeoutId.current = undefined;
    }
  }

  function enqueueServerValidation(referenceNumber: string) {
    timeoutId.current = setTimeout(function () {
      serverValidation(referenceNumber);
    }, 1000);
  }

  function setValid() {
    setResult({ isValid: true });
  }

  function setInvalid(
    referenceNumber: string,
    message: string,
    count?: number,
  ) {
    const result: Result = {
      isValid: false,
      message,
      count,
    };

    if (count && count > 0) {
      const referenceNumberType =
        locationContext === LocationContext.Pickup
          ? "pickupReferenceNumber"
          : "deliveryReferenceNumber";

      const url = `/view-all-shipments?${referenceNumberType}=${referenceNumber}&sort=PickupDate`;

      result.message = (
        <span>
          Reference number already used in {count > 1 ? "other" : "another"}{" "}
          <Link href={url} external>
            {count > 1 ? "shipments" : "shipment"}
          </Link>
        </span>
      );
    }

    setResult(result);
  }

  function noChanges(original: string | undefined, actual: string | undefined) {
    return isEmptyOrUndefined(original) && isEmptyOrUndefined(actual);
  }

  function validate(referenceNumber: string | undefined) {
    /*
     * Clear state
     */
    clearQueuedValidation();
    setResult({ ...invalidResult });

    if (referenceNumber === undefined || referenceNumber.trim() === "") {
      setValid();
      return;
    }

    /*
     * Local validation
     */
    const cleanReferenceNumber = referenceNumber.trim();
    const cleanOriginalReferenceNumber = originalReferenceNumber?.trim();
    if (noChanges(cleanOriginalReferenceNumber, cleanReferenceNumber)) {
      setValid();
      return;
    }

    if (cleanReferenceNumber.length < REFERENCE_NUMBER_MIN_CHARS) {
      setInvalid(
        referenceNumber,
        `Reference number should be at least ${REFERENCE_NUMBER_MIN_CHARS} characters`,
      );
      return;
    }

    /*
     * Server validation
     */
    enqueueServerValidation(referenceNumber);
  }

  return { isLoading, result: result, validate };
}
